import {
  type WorkspaceSaveBlockReason,
  workspaceSaveBlockReason,
} from "@src/features/agenticCodegen/workspace/saveAvailability";

import type { RuleSaveAction } from "./publishOnSave";

/**
 * Whether the rule page's Save is unavailable, and why.
 *
 * The rule page's Save persists two independent things: the rule's display
 * settings (written onto the rule row) and the code buffer (written as a
 * draft). The shared {@link workspaceSaveBlockReason} describes the second —
 * in particular its syntax term is the verdict of the Advanced tab's Monaco
 * worker on the code buffer. A Save that will not write the buffer must not
 * wait on that verdict: the worker re-derives it whenever its libraries or
 * companion models are re-registered, and a settings-only Save that followed
 * it went grey and blue again on its own with no network traffic at all. Nor
 * can it wait on a verdict that never arrives — an org without the Advanced
 * tab never mounts the editor, so the buffer is never checked.
 */
export interface RuleSaveAvailabilityInput {
  /** The saved settings moved underneath a local edit; see the conflict banner. */
  settingsConflict: boolean;
  /** Any pending edit on the page — code buffer or display settings. */
  isDirty: boolean;
  hasUnpublishedChanges: boolean;
  isChecking: boolean;
  comparisonFailed: boolean;
  /** The code buffer differs from the saved draft, so Save will write it. */
  isCodeDirty: boolean;
  /** `save_and_publish` writes the buffer before it publishes, dirty or not. */
  saveAction: RuleSaveAction;
  isReadOnly: boolean;
  isSavingDraft: boolean;
  isPublishing: boolean;
  isSyntaxValid: boolean;
  /** The verdict for the current code buffer has not landed yet. */
  isSyntaxPending: boolean;
}

export type RuleSaveBlockReason =
  | WorkspaceSaveBlockReason
  | "settings_conflict"
  | "comparison_pending"
  | "comparison_failed"
  | "nothing_to_save";

/** A saved Atlas draft can publish directly; local edits still save as drafts. */
export const resolveRuleSaveOperation = ({
  isDirty,
  hasUnpublishedChanges,
  saveAction,
}: Pick<
  RuleSaveAvailabilityInput,
  "isDirty" | "hasUnpublishedChanges" | "saveAction"
>): RuleSaveAction | "publish_saved_draft" =>
  !isDirty && hasUnpublishedChanges && saveAction === "save_draft_only"
    ? "publish_saved_draft"
    : saveAction;

export const ruleSaveBlockReason = ({
  settingsConflict,
  isDirty,
  hasUnpublishedChanges,
  isChecking,
  comparisonFailed,
  isCodeDirty,
  saveAction,
  isReadOnly,
  isSavingDraft,
  isPublishing,
  isSyntaxValid,
  isSyntaxPending,
}: RuleSaveAvailabilityInput): RuleSaveBlockReason | null => {
  if (settingsConflict) {
    return "settings_conflict";
  }
  if (!isDirty && saveAction === "save_draft_only") {
    if (comparisonFailed) {
      return "comparison_failed";
    }
    if (isChecking) {
      return "comparison_pending";
    }
    if (!hasUnpublishedChanges) {
      return "nothing_to_save";
    }
  }
  const writesCodeBuffer = isCodeDirty || saveAction === "save_and_publish";
  return workspaceSaveBlockReason({
    isReadOnly,
    isSavingDraft,
    isPublishing,
    isSyntaxValid: writesCodeBuffer ? isSyntaxValid : true,
    isSyntaxPending: writesCodeBuffer ? isSyntaxPending : false,
  });
};

/**
 * The hover copy for a blocked Save. `nothing_to_save` has none: the button
 * already reads "Draft saved", which is the reason.
 */
export const RULE_SAVE_BLOCK_HINT: Record<
  RuleSaveBlockReason,
  string | undefined
> = {
  settings_conflict:
    "The saved settings changed. Reload the saved settings before saving.",
  comparison_pending: "Checking the saved draft…",
  comparison_failed: "Reload to check the published version.",
  nothing_to_save: undefined,
  read_only: "Wait for Atlas to finish updating this rule.",
  saving: "Wait for your changes to finish saving.",
  publishing: "Wait for the current run to finish.",
  syntax_checking: "Checking the rule's TypeScript before it can be saved.",
  syntax_invalid: "Fix the TypeScript errors in the rule to save it.",
};
